/**
 * King Living Button Theme
 *
 * Comprehensive button styling system supporting:
 * - 3 button types: default, rounded, icon-only
 * - 4 visual modes: filled-light, filled-dark, outlined-light, outlined-dark, text
 * - 4 states: default, hover, disabled, focus
 * - 3 sizes: small, medium, large (responsive)
 *
 * @override Extends @salesforce/retail-react-app button theme
 */

import baseButton from '@salesforce/retail-react-app/app/theme/components/base/button';

export default {
  ...baseButton,

  baseStyle: {
    ...baseButton.baseStyle,
    fontWeight: 'medium',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2, // 8px gap between icon and text
    _focus: {
      boxShadow: 'none',
      outline: 'none',
    },
    _focusVisible: {
      boxShadow: '0 0 0 3px rgba(27, 26, 24, 0.2)',
    },
  },

  variants: {
    ...baseButton.variants,

    // ============================================
    // FILLED VARIANTS - Default Square Corners
    // ============================================

    'filled-light': {
      bg: 'brandColors.greyscale', // default: black
      color: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale', // border same as background
      _hover: {
        bg: 'white', // hover: white background
        color: 'brandColors.greyscale', // hover: black text
        borderColor: 'brandColors.greyscale', // hover: black border
        _disabled: {
          bg: 'brandColors.greyscale20', // disabled: light gray background
          color: 'brandColors.greyscale40', // disabled: lighter gray text
          borderColor: 'brandColors.greyscale20', // disabled: border same as background
        },
      },
      _active: {
        bg: 'white',
        color: 'brandColors.greyscale',
        borderColor: 'brandColors.greyscale',
      },
      _disabled: {
        bg: 'brandColors.greyscale20', // disabled: light gray background
        color: 'brandColors.greyscale40', // disabled: lighter gray text
        borderColor: 'brandColors.greyscale20', // disabled: border same as background
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'filled-dark': {
      bg: 'white', // default: white background
      color: 'brandColors.greyscale', // default: black text
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale', // default: black border (same as text)
      _hover: {
        bg: 'brandColors.greyscale', // hover: black background
        color: 'white', // hover: white text
        borderColor: 'white', // hover: white border (same as text)
        _disabled: {
          bg: 'brandColors.greyscale20', // disabled: light gray background
          color: 'brandColors.greyscale40', // disabled: lighter gray text
          borderColor: 'brandColors.greyscale20', // disabled: border same as background
        },
      },
      _active: {
        bg: 'brandColors.greyscale',
        color: 'white',
        borderColor: 'white',
      },
      _disabled: {
        bg: 'brandColors.greyscale20', // disabled: light gray background
        color: 'brandColors.greyscale40', // disabled: lighter gray text
        borderColor: 'brandColors.greyscale20', // disabled: border same as background
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    // ============================================
    // OUTLINED VARIANTS - Default Square Corners
    // ============================================

    'outlined-light': {
      bg: 'white', // default: white background
      color: 'brandColors.greyscale', // default: black text
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale', // default: black border (same as text)
      _hover: {
        bg: 'brandColors.greyscale', // hover: black background (same as border)
        color: 'white', // hover: white text
        borderColor: 'brandColors.greyscale', // hover: black border
        _disabled: {
          bg: 'white', // disabled: white background
          color: 'brandColors.greyscale30', // disabled: lighter gray text
          borderColor: 'brandColors.greyscale30', // disabled: lighter gray border (same as text)
        },
      },
      _active: {
        bg: 'brandColors.greyscale',
        color: 'white',
        borderColor: 'brandColors.greyscale',
      },
      _disabled: {
        bg: 'white', // disabled: white background
        color: 'brandColors.greyscale30', // disabled: lighter gray text
        borderColor: 'brandColors.greyscale30', // disabled: lighter gray border (same as text)
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'outlined-dark': {
      bg: 'transparent', // default: black background
      color: 'white', // default: white text
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'white', // default: white border (same as text)
      _hover: {
        bg: 'white', // hover: white background (same as border)
        color: 'brandColors.greyscale', // hover: black text
        borderColor: 'white', // hover: white border
        _disabled: {
          bg: 'brandColors.greyscale', // disabled: black background
          color: 'whiteAlpha.450', // disabled: lighter gray text
          borderColor: 'whiteAlpha.450', // disabled: lighter gray border (same as text)
        },
      },
      _active: {
        bg: 'white',
        color: 'brandColors.greyscale',
        borderColor: 'white',
      },
      _disabled: {
        bg: 'brandColors.greyscale', // disabled: black background
        color: 'whiteAlpha.450', // disabled: lighter gray text
        borderColor: 'whiteAlpha.450', // disabled: lighter gray border (same as text)
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    // ============================================
    // TEXT VARIANTS - Transparent Border
    // ============================================

    'text': {
      bg: 'transparent', // default: transparent background
      color: 'brandColors.greyscale', // default: black text
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'transparent', // transparent border
      _hover: {
        bg: 'transparent', // hover: no background change
        color: 'brandColors.greyscale', // hover: same text color (no change)
        borderColor: 'transparent', // hover: transparent border
        _disabled: {
          bg: 'transparent', // disabled: transparent background
          color: 'brandColors.greyscale30', // disabled: lighter gray text
          borderColor: 'transparent', // disabled: transparent border
        },
      },
      _active: {
        bg: 'transparent',
        color: 'brandColors.greyscale',
        borderColor: 'transparent',
      },
      _disabled: {
        bg: 'transparent', // disabled: transparent background
        color: 'brandColors.greyscale30', // disabled: lighter gray text
        borderColor: 'transparent', // disabled: transparent border
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'text-dark': {
      bg: 'transparent', // default: transparent background
      color: 'white', // default: white text
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'transparent', // transparent border
      _hover: {
        bg: 'transparent', // hover: no background change
        color: 'white', // hover: same text color (no change)
        borderColor: 'transparent', // hover: transparent border
        _disabled: {
          bg: 'transparent', // disabled: transparent background
          color: 'whiteAlpha.450', // disabled: lighter gray text
          borderColor: 'transparent', // disabled: transparent border
        },
      },
      _active: {
        bg: 'transparent',
        color: 'white',
        borderColor: 'transparent',
      },
      _disabled: {
        bg: 'transparent', // disabled: transparent background
        color: 'whiteAlpha.450', // disabled: lighter gray text
        borderColor: 'transparent', // disabled: transparent border
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    // ============================================
    // ROUNDED VARIANTS - Fully Rounded Corners
    // ============================================

    'button-rounded-filled-light': {
      bg: 'brandColors.greyscale',
      color: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale',
      borderRadius: '9999px',
      _hover: {
        bg: 'white',
        color: 'brandColors.greyscale',
        borderColor: 'brandColors.greyscale',
        _disabled: {
          bg: 'brandColors.greyscale20',
          color: 'brandColors.greyscale40',
          borderColor: 'brandColors.greyscale20',
        },
      },
      _active: {
        bg: 'white',
        color: 'brandColors.greyscale',
        borderColor: 'brandColors.greyscale',
      },
      _disabled: {
        bg: 'brandColors.greyscale20',
        color: 'brandColors.greyscale40',
        borderColor: 'brandColors.greyscale20',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'button-rounded-filled-dark': {
      bg: 'white',
      color: 'brandColors.greyscale',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale',
      borderRadius: '9999px',
      _hover: {
        bg: 'brandColors.greyscale',
        color: 'white',
        borderColor: 'white',
        _disabled: {
          bg: 'brandColors.greyscale20',
          color: 'brandColors.greyscale40',
          borderColor: 'brandColors.greyscale20',
        },
      },
      _active: {
        bg: 'brandColors.greyscale',
        color: 'white',
        borderColor: 'white',
      },
      _disabled: {
        bg: 'brandColors.greyscale20',
        color: 'brandColors.greyscale40',
        borderColor: 'brandColors.greyscale20',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'button-rounded-outlined-light': {
      bg: 'white',
      color: 'brandColors.greyscale',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale',
      borderRadius: '9999px',
      _hover: {
        bg: 'brandColors.greyscale',
        color: 'white',
        borderColor: 'brandColors.greyscale',
        _disabled: {
          bg: 'white',
          color: 'brandColors.greyscale30',
          borderColor: 'brandColors.greyscale30',
        },
      },
      _active: {
        bg: 'brandColors.greyscale',
        color: 'white',
        borderColor: 'brandColors.greyscale',
      },
      _disabled: {
        bg: 'white',
        color: 'brandColors.greyscale30',
        borderColor: 'brandColors.greyscale30',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'button-rounded-outlined-dark': {
      bg: 'brandColors.greyscale',
      color: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'white',
      borderRadius: '9999px',
      _hover: {
        bg: 'white',
        color: 'brandColors.greyscale',
        borderColor: 'white',
        _disabled: {
          bg: 'brandColors.greyscale',
          color: 'whiteAlpha.450',
          borderColor: 'whiteAlpha.450',
        },
      },
      _active: {
        bg: 'white',
        color: 'brandColors.greyscale',
        borderColor: 'white',
      },
      _disabled: {
        bg: 'brandColors.greyscale',
        color: 'whiteAlpha.450',
        borderColor: 'whiteAlpha.450',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'button-rounded-text': {
      bg: 'transparent',
      color: 'brandColors.greyscale',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      borderRadius: '9999px',
      _hover: {
        bg: 'transparent', // hover: no background change
        color: 'brandColors.greyscale', // hover: same text color
        borderColor: 'transparent',
        _disabled: {
          bg: 'transparent',
          color: 'brandColors.greyscale30',
          borderColor: 'transparent',
        },
      },
      _active: {
        bg: 'transparent',
        color: 'brandColors.greyscale',
        borderColor: 'transparent',
      },
      _disabled: {
        bg: 'transparent',
        color: 'brandColors.greyscale30',
        borderColor: 'transparent',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'button-rounded-text-dark': {
      bg: 'transparent',
      color: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      borderRadius: '9999px',
      _hover: {
        bg: 'transparent', // hover: no background change
        color: 'white', // hover: same text color
        borderColor: 'transparent',
        _disabled: {
          bg: 'transparent',
          color: 'whiteAlpha.450',
          borderColor: 'transparent',
        },
      },
      _active: {
        bg: 'transparent',
        color: 'white',
        borderColor: 'transparent',
      },
      _disabled: {
        bg: 'transparent',
        color: 'whiteAlpha.450',
        borderColor: 'transparent',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    // ============================================
    // ICON-ONLY VARIANTS - Square Icon Buttons
    // ============================================

    'button-icon-filled-light': {
      bg: 'brandColors.greyscale',
      color: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale',
      padding: 0,
      minWidth: 'auto',
      _hover: {
        bg: 'white',
        color: 'brandColors.greyscale',
        borderColor: 'brandColors.greyscale',
        _disabled: {
          bg: 'brandColors.greyscale20',
          color: 'brandColors.greyscale40',
          borderColor: 'brandColors.greyscale20',
        },
      },
      _active: {
        bg: 'white',
        color: 'brandColors.greyscale',
        borderColor: 'brandColors.greyscale',
      },
      _disabled: {
        bg: 'brandColors.greyscale20',
        color: 'brandColors.greyscale40',
        borderColor: 'brandColors.greyscale20',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'button-icon-filled-dark': {
      bg: 'white',
      color: 'brandColors.greyscale',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale',
      padding: 0,
      minWidth: 'auto',
      _hover: {
        bg: 'brandColors.greyscale',
        color: 'white',
        borderColor: 'white',
        _disabled: {
          bg: 'brandColors.greyscale20',
          color: 'brandColors.greyscale40',
          borderColor: 'brandColors.greyscale20',
        },
      },
      _active: {
        bg: 'brandColors.greyscale',
        color: 'white',
        borderColor: 'white',
      },
      _disabled: {
        bg: 'brandColors.greyscale20',
        color: 'brandColors.greyscale40',
        borderColor: 'brandColors.greyscale20',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'button-icon-outlined-light': {
      bg: 'white',
      color: 'brandColors.greyscale',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale',
      padding: 0,
      minWidth: 'auto',
      _hover: {
        bg: 'brandColors.greyscale',
        color: 'white',
        borderColor: 'brandColors.greyscale',
        _disabled: {
          bg: 'white',
          color: 'brandColors.greyscale30',
          borderColor: 'brandColors.greyscale30',
        },
      },
      _active: {
        bg: 'brandColors.greyscale',
        color: 'white',
        borderColor: 'brandColors.greyscale',
      },
      _disabled: {
        bg: 'white',
        color: 'brandColors.greyscale30',
        borderColor: 'brandColors.greyscale30',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'button-icon-outlined-dark': {
      bg: 'brandColors.greyscale',
      color: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'white',
      padding: 0,
      minWidth: 'auto',
      _hover: {
        bg: 'white',
        color: 'brandColors.greyscale',
        borderColor: 'white',
        _disabled: {
          bg: 'brandColors.greyscale',
          color: 'whiteAlpha.450',
          borderColor: 'whiteAlpha.450',
        },
      },
      _active: {
        bg: 'white',
        color: 'brandColors.greyscale',
        borderColor: 'white',
      },
      _disabled: {
        bg: 'brandColors.greyscale',
        color: 'whiteAlpha.450',
        borderColor: 'whiteAlpha.450',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'button-icon-text': {
      bg: 'transparent',
      color: 'brandColors.greyscale',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      padding: 0,
      minWidth: 'auto',
      _hover: {
        bg: 'transparent', // hover: no background change
        color: 'brandColors.greyscale', // hover: same text color
        borderColor: 'transparent',
        _disabled: {
          bg: 'transparent',
          color: 'brandColors.greyscale30',
          borderColor: 'transparent',
        },
      },
      _active: {
        bg: 'transparent',
        color: 'brandColors.greyscale',
        borderColor: 'transparent',
      },
      _disabled: {
        bg: 'transparent',
        color: 'brandColors.greyscale30',
        borderColor: 'transparent',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'button-icon-text-dark': {
      bg: 'transparent',
      color: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      padding: 0,
      minWidth: 'auto',
      _hover: {
        bg: 'transparent', // hover: no background change
        color: 'white', // hover: same text color
        borderColor: 'transparent',
        _disabled: {
          bg: 'transparent',
          color: 'whiteAlpha.450',
          borderColor: 'transparent',
        },
      },
      _active: {
        bg: 'transparent',
        color: 'white',
        borderColor: 'transparent',
      },
      _disabled: {
        bg: 'transparent',
        color: 'whiteAlpha.450',
        borderColor: 'transparent',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    // ============================================
    // LINEN VARIANT - Special King Living Style
    // ============================================

    'filled-linen': {
      bg: 'brandColors.primaryLinen100', // default: linen
      color: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.primaryLinen100',
      _hover: {
        bg: 'brandColors.primaryDarkLinen', // hover: dark linen
        borderColor: 'brandColors.primaryDarkLinen',
        _disabled: {
          bg: 'brandColors.primaryLinen15', // disabled: 15% linen
          borderColor: 'brandColors.primaryLinen15',
          color: 'white',
        },
      },
      _active: {
        bg: 'brandColors.primaryDarkLinen',
        borderColor: 'brandColors.primaryDarkLinen',
      },
      _disabled: {
        bg: 'brandColors.primaryLinen15',
        borderColor: 'brandColors.primaryLinen15',
        color: 'white',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },

    'button-rounded-filled-linen': {
      bg: 'brandColors.primaryLinen100',
      color: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.primaryLinen100',
      borderRadius: '9999px',
      _hover: {
        bg: 'brandColors.primaryDarkLinen',
        borderColor: 'brandColors.primaryDarkLinen',
        _disabled: {
          bg: 'brandColors.primaryLinen15',
          borderColor: 'brandColors.primaryLinen15',
          color: 'white',
        },
      },
      _active: {
        bg: 'brandColors.primaryDarkLinen',
        borderColor: 'brandColors.primaryDarkLinen',
      },
      _disabled: {
        bg: 'brandColors.primaryLinen15',
        borderColor: 'brandColors.primaryLinen15',
        color: 'white',
        cursor: 'not-allowed',
        opacity: 1,
      },
    },
  },

  sizes: {
    ...baseButton.sizes,

    // ============================================
    // SIZE: SMALL
    // ============================================
    sm: {
      // Desktop
      'height': {base: 9, md: 10}, // mobile: 36px, desktop: 40px
      'minWidth': {base: 9, md: 10},
      'px': {base: 3, md: 4}, // mobile: 12px, desktop: 16px
      'fontSize': {base: 'sm', md: 'sm'}, // 14px
      'lineHeight': '1.2',

      // Icon-only buttons (square)
      '&[class*="button-icon"]': {
        width: {base: 8, md: 8}, // 32px
        height: {base: 8, md: 8}, // 32px
        minWidth: {base: 8, md: 8},
        px: 0,
      },
    },

    // ============================================
    // SIZE: MEDIUM (Default)
    // ============================================
    md: {
      // Desktop
      'height': {base: 11, md: 12}, // mobile: 44px, desktop: 48px
      'minWidth': {base: 11, md: 12},
      'px': {base: 5, md: 6}, // mobile: 20px, desktop: 24px
      'fontSize': {base: 'md', md: 'md'}, // 16px
      'lineHeight': '1.2',

      // Icon-only buttons (square)
      '&[class*="button-icon"]': {
        width: {base: 10, md: 10}, // 40px
        height: {base: 10, md: 10}, // 40px
        minWidth: {base: 10, md: 10},
        px: 0,
      },
    },

    // ============================================
    // SIZE: LARGE
    // ============================================
    lg: {
      // Desktop
      'height': {base: 12.5, md: 14}, // mobile: 50px, desktop: 56px
      'minWidth': {base: 12.5, md: 14},
      'px': {base: 6, md: 8}, // mobile: 24px, desktop: 32px
      'fontSize': {base: 'lg', md: 'lg'}, // 18px
      'lineHeight': '1.2',

      // Icon-only buttons (square)
      '&[class*="button-icon"]': {
        width: {base: 12, md: 12}, // 48px
        height: {base: 12, md: 12}, // 48px
        minWidth: {base: 12, md: 12},
        px: 0,
      },
    },
  },

  defaultProps: {
    variant: 'filled-light',
    size: 'md',
    colorScheme: 'blue', // Keep for backward compatibility
  },
};
